/**
 * @fileoverview Entrypoint for the Enterprise Listings Launch Flow.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {Routes} from '/src/com/yext/listingslaunchstorm/js/routes';

import {ExternalAccountsContextProvider} from '/js/yext/externalaccounts/app/listingslaunch/externalaccountscontextprovider';
import {StormContextProvider} from '/src/com/yext/listingslaunchstorm/js/common/contexts/stormcontextprovider';
import setConfirmationDialog from '/src/com/yext/listingslaunchstorm/js/common/hooks/setconfirmationdialog';
import {useAjax} from '/src/com/yext/listingslaunchstorm/js/common/hooks/useajax';
import {LaunchProgress} from '/src/com/yext/listingslaunchstorm/js/enterprise/launchprogress/launchprogress';
import {ViewEnterpriseLaunchOptions} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/viewenterpriselaunchoptions';
import {submit} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/datasource/submit';
import {EnterpriseTaskflow} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/taskflow';
import {SmallDialog} from '/ui/components/modal/dialog';
import {slowRegisterComponentForJsonData} from '/ui/lib/easyreact';

/**
 * @param {{
*  options: !ViewEnterpriseLaunchOptions,
* }} props
*/
export function EnterpriseLaunch({options}) {
  const hasChooseEntitiesAndListingsConfigSetup = options.hasChooseEntitiesAndListingsConfigSetup;
  const [configuration, setConfiguration] = React.useState(undefined);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const {status, refetch} = useAjax(false, submit, configuration);
  React.useEffect(() => {
    setConfirmationDialog(status !== 'loading');
  }, [status]);

  const onSave = configuration => {
    setConfiguration(configuration);
    setIsConfirming(true);
  };

  if (status === 'success') {
    window.location.href = Routes.allListings();
  } else if (status) {
    return (<LaunchProgress status={status} />);
  } else {
    return (
      <StormContextProvider isViewAsEmployee={options.isViewAsEmployee}>
        <ExternalAccountsContextProvider defaultPublisherIdToExternalAccountId={new Map()}>
          <EnterpriseTaskflow
            onSaveConfiguration={onSave}
            publisherIds={options.publisherIds}
            hasChooseEntitiesAndListingsConfigSetup={hasChooseEntitiesAndListingsConfigSetup}
          />
          <ConfirmationDialog
            show={isConfirming}
            onConfirm={refetch}
            onCancel={() => setIsConfirming(false)}
          />
        </ExternalAccountsContextProvider>
      </StormContextProvider>
    );
  }
}

/**
 * @param {{
 *   show: boolean,
 *   onConfirm: function(): void,
 *   onCancel: function(): void,
 * }} props
 * @returns {React.ReactNode}
 */
function ConfirmationDialog({
  show,
  onConfirm,
  onCancel,
}) {
  return (
    <SmallDialog
      title={yext.msg('Listings Launch Confirmation')}
      callToAction={{
        cancel: {
          type: 'cancel',
          text: yext.msg('Cancel'),
          callback: onCancel,
        },
        primary: {
          type: 'active',
          text: yext.msg('Confirm'),
          callback: onConfirm,
        },
      }}
      show={show}
      onHideDialog={onCancel}
    >
      <p>{yext.msg('You are about to launch your Listings setup.')}</p>
      <p>{yext.msg('Press Confirm if you are ready to launch your Listings. Otherwise, press Cancel to return to the setup.')}</p>
    </SmallDialog>
  );
}

slowRegisterComponentForJsonData('EnterpriseLaunch', EnterpriseLaunch);
